import React, { useState, useEffect, useCallback, useRef } from "react";
import "./App.css";
import Camera from "./components/Camera";
import AdminButton from "./components/AdminButton";
import { identifyUser } from "./services/api";
import { useConfig } from "./hooks/useConfig";
import { naturalizeTime } from "./utils/speechUtils";

function App() {
  const { config, updateConfig } = useConfig();

  // States: 'scanning', 'result'
  const [viewState, setViewState] = useState('scanning');
  const [appState, setAppState] = useState(null);
  const isProcessing = useRef(false);
  const cameraRef = useRef(null);

  const resetToScan = () => {
    setAppState(null);
    setViewState('scanning');
  };

  // Volver a escanear automáticamente tras mostrar resultado
  useEffect(() => {
    if (viewState === 'result' && config.resetTimeoutSeconds > 0) {
      const t = setTimeout(resetToScan, config.resetTimeoutSeconds * 1000);
      return () => clearTimeout(t);
    }
  }, [viewState, config.resetTimeoutSeconds]); // eslint-disable-line

  const handleCapture = useCallback(async (base64Image) => {
    if (isProcessing.current) return;
    isProcessing.current = true;
    try {
      const response = await identifyUser(base64Image, config.webhookUrl);

      if (response && response.status === 'user_found') {
        setAppState({
          ...response,
          assets: {
            personImg: "/assets/ana_caregiver.png",
            personName: "Ana",
            roomImg: "/assets/physio_room.png",
            roomName: "Sala",
            ...(response.assets || {})
          }
        });
        setViewState('result');

        const msg = response.message || `${response.ui?.title}. ${response.ui?.subtitle}`;
        speakMessage(msg);
      } else {
        // Sin cara → esperar retryDelaySeconds antes de reintentar
        await new Promise(r => setTimeout(r, config.retryDelaySeconds * 1000));
      }
    } catch (error) {
      // Sin cara (error HTTP de Rekognition) → esperar antes de reintentar
      await new Promise(r => setTimeout(r, config.retryDelaySeconds * 1000));
    } finally {
      isProcessing.current = false;
    }
  }, [config.webhookUrl, config.speech, config.retryDelaySeconds]); // eslint-disable-line

  const speakMessage = (msg) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(naturalizeTime(msg));
      utterance.lang = config.speech.lang;
      utterance.rate = config.speech.rate;
      utterance.volume = config.speech.volume;
      window.speechSynthesis.speak(utterance);
    }
  };

  return (
    <div className="app-container">

      {/* Vista de escaneo: logo + cámara vertical + botón */}
      <div className={`scan-view ${viewState === 'scanning' ? 'scan-view--visible' : 'scan-view--hidden'}`}>
        <img src="/assets/logo-dano-cerebral-ateneu.jpg" alt="Ateneu" className="scan-logo" />
        <Camera
          ref={cameraRef}
          onCapture={handleCapture}
          paused={viewState !== 'scanning'}
          intervalSeconds={config.captureIntervalSeconds}
          autoScan={config.autoScanEnabled}
        />
        {!config.autoScanEnabled && (
          <button className="capture-button" onClick={() => cameraRef.current?.capture()}>
            📸 Escanear
          </button>
        )}
      </div>

      {viewState === 'result' && appState && (
        <div className="greeting-card">
          <h1 className="greeting-title">{appState.ui?.title}</h1>
          <div className="time-display">
            {appState.currentContext ? `Son las ${appState.currentContext.time}` : ''}
          </div>
          <div className="task-display">
            {appState.currentContext?.statusInfo || appState.ui?.subtitle}
          </div>

          {appState.status === 'user_found' && (
            <div className="visual-context">
              <div className="context-card">
                <img
                  src={appState.assets.personImg}
                  alt="Caregiver"
                  className="context-image"
                  onError={(e) => { e.target.style.display = 'none' }}
                />
                <p className="context-label">{appState.assets.personName}</p>
              </div>
              <div className="context-card">
                <img
                  src={appState.assets.roomImg}
                  alt="Room"
                  className="context-image"
                  onError={(e) => { e.target.style.display = 'none' }}
                />
                <p className="context-label">{appState.assets.roomName}</p>
              </div>
            </div>
          )}

          <p style={{ color: '#aaa', fontSize: '0.9rem', marginTop: '1.5rem' }}>
            Volviendo al escáner en {config.resetTimeoutSeconds} segundos...
          </p>
        </div>
      )}

      <AdminButton config={config} updateConfig={updateConfig} />
    </div>
  );
}

export default App;
